'use client';

import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselPrevious,
} from "@/components/ui/carousel";
import { useEffect, useState } from 'react';
import { propertyServiceAPI } from '@/lib/api';
import Image from 'next/image';
import Link from 'next/link';

// Services data structure
const servicesData = [
    {
        id: 1,
        title: "Fitness & Wellness",
        image: "https://images.pexels.com/photos/4056723/pexels-photo-4056723.jpeg",
        description: "State-of-the-art gym equipment and personal training"
    },
    {
        id: 2,
        title: "Spa & Relaxation",
        image: "https://images.pexels.com/photos/3771069/pexels-photo-3771069.jpeg",
        description: "Luxurious spa treatments and massage therapy"
    },
    {
        id: 3,
        title: "Private Dining",
        image: "https://images.pexels.com/photos/958545/pexels-photo-958545.jpeg",
        description: "Personal chef services and gourmet dining"
    },
    {
        id: 4,
        title: "Swimming Pool",
        image: "https://images.pexels.com/photos/261102/pexels-photo-261102.jpeg",
        description: "Infinity pools with stunning views"
    },
    {
        id: 5,
        title: "Concierge Service",
        image: "https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg",
        description: "24/7 personalized assistance and planning"
    },
    {
        id: 6,
        title: "Transportation",
        image: "https://images.pexels.com/photos/3802510/pexels-photo-3802510.jpeg",
        description: "Luxury car rentals and airport transfers"
    },
    {
        id: 7,
        title: "Housekeeping",
        image: "https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg",
        description: "Daily cleaning and turndown service"
    },
    {
        id: 8,
        title: "Entertainment",
        image: "https://images.pexels.com/photos/1540406/pexels-photo-1540406.jpeg",
        description: "Home theater systems and gaming consoles"
    }
];

export default function Services() {
    const [servicesList, setServicesList] = useState<any[]>([]);

    useEffect(() => {
        fetchServices();
    }, []);

    const fetchServices = async () => {
        try {
            const resp = await propertyServiceAPI.getFrontendList();
            const raw = (resp as any)?.data;
            const list = (raw?.data ?? raw?.items ?? raw?.results ?? raw) as any[];
            setServicesList(list);
        } catch (error) {
            console.error('Error fetching services:', error);
        }
    };

    return (
        <section className="py-20 bg-gray-50">
            <div className="container mx-auto px-4">
                <div className="grid lg:grid-cols-2 gap-12 items-center">
                    {/* Left Section - Text Content */}
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="space-y-6"
                    >
                        {/* Small Label */}
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-px bg-[#e6b400]"></div>
                            <span className="text-sm font-medium uppercase tracking-wider">
                                Amenities & Services
                            </span>
                        </div>

                        {/* Main Title */}
                        <h2 className="text-3xl lg:text-4xl font-semibold text-gray-900 leading-tight mb-6">
                            Seamlessly add bespoke services & amenities to your stay
                        </h2>

                        {/* Description */}
                        <p className="text-lg text-gray-600 leading-relaxed">
                            Luxury villas need luxury services. Our in-house concierge team will assist you in tailoring your stay to create an unforgettable holiday.
                        </p>

                        {/* CTA Button */}
                        <Button
                        // className="text-white px-8 py-3 rounded-lg font-medium"
                        >
                            <span className="mr-2">View Services</span>
                            {/* <div className="w-6 h-6 border border-white/30 rounded flex items-center justify-center">
                            </div> */}
                            <ArrowRight className="w-3 h-3" />
                        </Button>
                    </motion.div>

                    {/* Right Section - Service Images Carousel */}
                    <motion.div
                        initial={{ opacity: 0, x: 50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        viewport={{ once: true }}
                        className="relative"
                    >
                        <Carousel
                            opts={{
                                align: "start",
                                loop: true,
                            }}
                            className="w-full"
                        >
                            <CarouselContent className="-ml-2 md:-ml-4">
                                {servicesList?.map((service) => (
                                    <CarouselItem key={service.id} className="pl-2 md:pl-4 basis-1/1 md:basis-1/2 lg:basis-1/3">
                                        <div className="relative h-80 rounded-lg overflow-hidden shadow-lg group cursor-pointer transition-transform duration-300 hover:scale-105">
                                            <Link href={`/services/${service.slug}`}>
                                                <Image
                                                    src={service?.media?.gallery[0]}
                                                    alt={service?.name}
                                                    width={320}
                                                    height={240}
                                                    className="w-full h-full object-cover"
                                                />
                                                <div className="absolute bottom-4 left-4 right-4">
                                                    <h3 className="text-white font-semibold text-lg mb-1">{service.name}</h3>
                                                    <p className="text-white/90 text-sm leading-relaxed line-clamp-2">{service.description}</p>
                                                </div>
                                            </Link>
                                        </div>
                                    </CarouselItem>
                                ))}
                            </CarouselContent>

                            {/* Navigation Buttons */}
                            <CarouselPrevious className="left-2 w-8 h-8 bg-white/80 hover:bg-white border-0 text-gray-700 shadow-lg" />
                            <CarouselNext className="right-2 w-8 h-8 bg-white/80 hover:bg-white border-0 text-gray-700 shadow-lg" />
                        </Carousel>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
